// Browser-side typed API client. Implements the same ApiSurface shape
// the Electron preload used to expose, but talks to the Fastify
// backend over HTTPS instead of IPC.
//
// Live events (transcription status / utterance / error / ingest) flow
// over a single SSE stream at /api/events. The subscription helpers
// share one EventSource across all subscribers.

import type {
  ApiSurface,
  CaptureRow,
  CaptureSession,
  ContextField,
  ExportOptions,
  RowEditPatch,
  SessionSummary,
  SettingsSnapshot,
  TranscriptEvent,
  TranscriptionError,
  TranscriptionMode,
  TranscriptionProviderId,
  TranscriptionStatus,
} from '@shared/types.js';

interface UserPublic {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'user';
  mustChangePassword: boolean;
}

export interface AdminUser extends UserPublic {
  createdAt: string;
  disabledAt: string | null;
}

async function jsonFetch<T>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const isFormData = init.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  // Let the browser set the multipart boundary on FormData; otherwise JSON.
  if (!isFormData && init.body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers,
  });
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown;
    try { parsed = JSON.parse(text); } catch { parsed = { error: text }; }
    const e = new Error(`HTTP ${res.status}: ${(parsed as { message?: string; error?: string }).message ?? (parsed as { error?: string }).error ?? text}`);
    (e as unknown as { status: number; payload: unknown }).status = res.status;
    (e as unknown as { status: number; payload: unknown }).payload = parsed;
    throw e;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function blobFetch(url: string): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  const dispo = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(dispo);
  return { blob: await res.blob(), filename: m ? m[1]! : null };
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

// ----- Auth -----

export const auth = {
  async login(email: string, password: string): Promise<{ user: UserPublic }> {
    return jsonFetch('/api/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async logout(): Promise<void> {
    await jsonFetch('/api/logout', { method: 'POST' });
  },
  async me(): Promise<{ user: UserPublic } | null> {
    try {
      return await jsonFetch<{ user: UserPublic }>('/api/me');
    } catch (err) {
      if ((err as { status?: number }).status === 401) return null;
      throw err;
    }
  },
  async changePassword(current: string, next: string): Promise<void> {
    await jsonFetch('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    });
  },
};

// ----- Admin -----

export const admin = {
  async listUsers(): Promise<{ users: AdminUser[] }> {
    return jsonFetch('/api/admin/users');
  },
  async createUser(args: {
    email: string;
    displayName: string;
    role: 'admin' | 'user';
  }): Promise<{ user: AdminUser; tempPassword: string }> {
    return jsonFetch('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(args),
    });
  },
  async resetPassword(userId: string): Promise<{ tempPassword: string }> {
    return jsonFetch(`/api/admin/users/${userId}/reset-password`, { method: 'POST' });
  },
  async setDisabled(userId: string, disabled: boolean): Promise<void> {
    await jsonFetch(`/api/admin/users/${userId}/disabled`, {
      method: 'POST',
      body: JSON.stringify({ disabled }),
    });
  },
  async setRole(userId: string, role: 'admin' | 'user'): Promise<void> {
    await jsonFetch(`/api/admin/users/${userId}/role`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
  },
  async setDisplayName(userId: string, displayName: string): Promise<void> {
    await jsonFetch(`/api/admin/users/${userId}/display-name`, {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  },
  async listAllSessions(): Promise<{
    sessions: Array<SessionSummary & { userId: string | null }>;
  }> {
    return jsonFetch('/api/admin/sessions');
  },
  async loadSession(id: string): Promise<{
    session: CaptureSession;
    rows: CaptureRow[];
    events: TranscriptEvent[];
  }> {
    return jsonFetch(`/api/admin/sessions/${id}`);
  },
};

// ----- ApiSurface implementation (used by the existing components) -----

export const api: ApiSurface = {
  async listSessions() {
    const { sessions } = await jsonFetch<{ sessions: SessionSummary[] }>('/api/sessions');
    return sessions;
  },
  async createSession(name) {
    const { session } = await jsonFetch<{ session: CaptureSession }>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return session;
  },
  async openSession(id) {
    return jsonFetch(`/api/sessions/${id}`);
  },
  async renameSession(id, name) {
    await jsonFetch(`/api/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },
  async deleteSession(id) {
    await jsonFetch(`/api/sessions/${id}`, { method: 'DELETE' });
  },
  async deleteAllSessions() {
    return jsonFetch<{ deleted: number }>(`/api/sessions`, { method: 'DELETE' });
  },
  async ingestLine(sessionId, rawText, resolvesEventId) {
    return jsonFetch(`/api/sessions/${sessionId}/ingest-line`, {
      method: 'POST',
      body: JSON.stringify({ rawText, resolvesEventId }),
    });
  },
  async ingestBlock(sessionId, rawText) {
    return jsonFetch(`/api/sessions/${sessionId}/ingest-block`, {
      method: 'POST',
      body: JSON.stringify({ rawText }),
    });
  },
  async editRow(sessionId, rowId, patch: RowEditPatch) {
    return jsonFetch(`/api/sessions/${sessionId}/rows/${rowId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  async deleteRow(sessionId, rowId) {
    return jsonFetch(`/api/sessions/${sessionId}/rows/${rowId}`, {
      method: 'DELETE',
    });
  },
  async addRow(sessionId, draft) {
    return jsonFetch(`/api/sessions/${sessionId}/rows`, {
      method: 'POST',
      body: JSON.stringify(draft),
    });
  },
  async reorderRows(sessionId, orderedIds) {
    return jsonFetch(`/api/sessions/${sessionId}/rows/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  },
  async setContext(sessionId, field: ContextField, value) {
    const { session } = await jsonFetch<{ session: CaptureSession }>(
      `/api/sessions/${sessionId}/context`,
      { method: 'POST', body: JSON.stringify({ field, value }) },
    );
    return session;
  },
  async getSettings() {
    const { settings } = await jsonFetch<{ settings: SettingsSnapshot }>('/api/settings');
    return settings;
  },
  async saveSettings(settings) {
    const { settings: s } = await jsonFetch<{ settings: SettingsSnapshot }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return s;
  },
  async exportSession(sessionId, options: ExportOptions) {
    const url = `/api/sessions/${sessionId}/export?mode=${encodeURIComponent(options.mode)}&format=${encodeURIComponent(options.format)}`;
    const { blob, filename } = await blobFetch(url);
    triggerDownload(blob, filename ?? `session-${sessionId}.${options.format}`);
    return { written: 0 };
  },
  // ----- Transcription (key never reaches the browser) -----
  async transcriptionHasKey() {
    const { openaiConfigured } = await jsonFetch<{ openaiConfigured: boolean }>(
      '/api/transcription/config',
    );
    return openaiConfigured;
  },
  async transcriptionStatus() {
    const { status } = await jsonFetch<{ status: TranscriptionStatus }>(
      '/api/transcription/status',
    );
    return status;
  },
  async transcriptionStartLive(opts) {
    await jsonFetch('/api/transcription/start', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  },
  async transcriptionStopLive() {
    await jsonFetch('/api/transcription/stop', { method: 'POST' });
  },
  async transcriptionPushAudio(payload) {
    const fd = new FormData();
    fd.append('sessionId', payload.sessionId);
    fd.append('audio', new Blob([payload.data], { type: payload.mimeType }), 'chunk.webm');
    const res = await fetch('/api/transcription/audio', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const body = (await res.json()) as {
      ok: boolean;
      text: string;
      hallucinated: boolean;
      rowsAdded: number;
      events: number;
    };
    return {
      text: body.text,
      hallucinated: body.hallucinated,
      rowsAdded: body.rowsAdded,
      events: body.events,
    };
  },
  onTranscriptionStatus(handler: (status: TranscriptionStatus) => void) {
    return events.subscribe<TranscriptionStatus>('status', handler);
  },
  onTranscriptionError(handler: (err: TranscriptionError) => void) {
    return events.subscribe<TranscriptionError>('error', handler);
  },
};

// ----- Browser-only helper for file uploads (called by the store) -----

export async function uploadAudioFile(opts: {
  sessionId: string;
  provider: TranscriptionProviderId;
  file: File;
  mode?: TranscriptionMode;
}): Promise<{ rowsAdded: number; events: number }> {
  const fd = new FormData();
  fd.append('sessionId', opts.sessionId);
  fd.append('provider', opts.provider);
  fd.append('audio', opts.file, opts.file.name);
  return jsonFetch('/api/transcription/transcribe-file', {
    method: 'POST',
    body: fd,
  });
}

// ----- SSE multiplexer -----

class EventBus {
  private es: EventSource | null = null;
  private channels = new Map<string, Set<(payload: unknown) => void>>();

  ensure(): void {
    if (this.es) return;
    this.es = new EventSource('/api/events');
    for (const ch of this.channels.keys()) this.bind(ch);
    this.es.onerror = () => {
      // Browser auto-reconnects. Nothing to do.
    };
  }

  private bind(channel: string): void {
    if (!this.es) return;
    this.es.addEventListener(channel, (ev) => {
      const handlers = this.channels.get(channel);
      if (!handlers) return;
      let data: unknown;
      try { data = JSON.parse((ev as MessageEvent).data); } catch { data = (ev as MessageEvent).data; }
      for (const h of handlers) h(data);
    });
  }

  subscribe<T>(channel: string, handler: (payload: T) => void): () => void {
    let set = this.channels.get(channel);
    if (!set) {
      set = new Set();
      this.channels.set(channel, set);
      this.ensure();
      this.bind(channel);
    } else {
      this.ensure();
    }
    const wrapped = (p: unknown) => handler(p as T);
    set.add(wrapped);
    return () => {
      set!.delete(wrapped);
    };
  }

  close(): void {
    this.es?.close();
    this.es = null;
    this.channels.clear();
  }
}

export const events = new EventBus();
